import type { Annotation } from '@rossum/api-client/annotations';
import type { Document } from '@rossum/api-client/documents';
import type { Email } from '@rossum/api-client/email';
import type { Label } from '@rossum/api-client/labels';
import type { Note } from '@rossum/api-client/notes';
import type { Page } from '@rossum/api-client/pages';
import type { Queue } from '@rossum/api-client/queues';
import type { SuggestedEdit } from '@rossum/api-client/suggestedEdit';
import type { User } from '@rossum/api-client/users';
import { snakeToCamel } from '@rossum/api-client/utils';
import type { CamelToSnake } from '@rossum/api-client/utils';
import type { Workspace } from '@rossum/api-client/workspaces';
import { VariableCompletion } from '@rossum/components/variable-suggestions/types';

type ContextSideload = {
  accessor: string;
  isList?: boolean;
  fieldShape: readonly string[];
  meta: string;
  description: string;
};

// The snake_case top-level column names of an api-client model type. Adding
// `satisfies readonly ModelColumns<SomeModel>[]` to a column list cross-checks
// the hand-written columns against the model at the type level — a renamed or
// removed field turns the stale entry into a compile error — while `as const`
// keeps the literal tuple type and the lists stay plain data with no runtime
// dependency on the api-client schemas.
type ModelColumns<T> = CamelToSnake<Extract<keyof T, string>>;

const queueKeys = [
  'id',
  'name',
  'url',
  'workspace',
  'connector',
  'webhooks',
  'hooks',
  'schema',
  'inbox',
  'users',
  'session_timeout',
  'rir_url',
  'rir_params',
  'counts',
  'default_score_threshold',
  'automation_enabled',
  'automation_level',
  'locale',
  'metadata',
  'use_confirmed_state',
  'archive_enabled',
  'settings',
  'document_lifetime',
  'dedicated_engine',
  'generic_engine',
  'engine',
  'status',
  'workflows',
  'training_enabled',
  'delete_after',
  'quality_spot_check_percentage',
] as const satisfies readonly ModelColumns<Queue>[];

const workspaceKeys = [
  'id',
  'name',
  'url',
  'autopilot',
  'organization',
  'queues',
  'metadata',
] as const satisfies readonly ModelColumns<Workspace>[];

const annotationKeys = [
  'document',
  'id',
  'queue',
  'schema',
  'relations',
  'pages',
  'creator',
  'modifier',
  'created_at',
  'modified_at',
  'confirmed_at',
  'export_failed_at',
  'exported_at',
  'deleted_at',
  'rejected_at',
  'confirmed_by',
  'deleted_by',
  'exported_by',
  'rejected_by',
  'assigned_at',
  'status',
  'rir_poll_id',
  'messages',
  'einvoice',
  'url',
  'time_spent',
  'metadata',
  'automated',
  'suggested_edit',
  'related_emails',
  'email',
  'automation_blocker',
  'email_thread',
  'organization',
  'automatically_rejected',
  'prediction',
  'has_email_thread_with_replies',
  'has_email_thread_with_new_replies',
  'assignees',
  'labels',
  'restricted_access',
  'training_enabled',
] as const satisfies readonly Exclude<ModelColumns<Annotation>, 'content'>[];

const documentKeys = [
  'id',
  'url',
  // @ts-expect-error - intentional `s3Name` -> `s3_name` display divergence
  's3_name',
  'parent',
  'email',
  'mime_type',
  'creator',
  'created_at',
  'arrived_at',
  'original_file_name',
  'content',
  'attachment_status',
  'metadata',
  'annotations',
] as const satisfies readonly ModelColumns<Document>[];

const emailKeys = [
  'id',
  'url',
  'queue',
  'inbox',
  'documents',
  'parent',
  'children',
  'created_at',
  'subject',
  'to',
  'cc',
  'bcc',
  'body_text_plain',
  'body_text_html',
  'metadata',
  'type',
  'annotation_counts',
  'annotations',
  'creator',
  'last_thread_email_created_at',
  'filtered_out_document_count',
  'labels',
  'email_thread',
  'related_annotations',
  'related_documents',
  'from',
] as const satisfies readonly ModelColumns<Email>[];

const suggestedEditKeys = [
  'id',
  'url',
  'annotation',
  'documents',
] as const satisfies readonly ModelColumns<SuggestedEdit>[];

const labelKeys = [
  'id',
  'url',
  'name',
  'color',
] as const satisfies readonly ModelColumns<Label>[];

const noteKeys = [
  'id',
  'url',
  'type',
  'metadata',
  'content',
  'created_at',
  'creator',
  'modified_at',
  'modifier',
  'annotation',
] as const satisfies readonly ModelColumns<Note>[];

const pageKeys = [
  'id',
  'annotation',
  'number',
  'rotation_deg',
  'mime_type',
  // @ts-expect-error - intentional `s3Name` -> `s3_name` display divergence
  's3_name',
  'url',
  'content',
  'width',
  'height',
  'metadata',
] as const satisfies readonly ModelColumns<Page>[];

const userKeys = [
  'id',
  'url',
  'first_name',
  'last_name',
  'email',
  'email_verified',
  'date_joined',
  'username',
  'groups',
  'organization',
  'queues',
  'is_active',
  'last_login',
  'ui_settings',
  'metadata',
  'oidc_id',
  'auth_type',
  'deleted',
  'phone_number',
] as const satisfies readonly ModelColumns<User>[];

// `relations` is a discriminated union in @rossum/api-client (no flat `.shape`)
// and `automation_blocker` has no matching model, so their columns are listed
// as plain literals without a schema cross-check.
const relationKeys = [
  'id',
  'type',
  'key',
  'parent',
  'annotations',
  'url',
] as const;

const automationBlockerKeys = ['id', 'url', 'annotation', 'content'] as const;

export const CONTEXT_SIDELOADS = [
  {
    accessor: 'queue',
    fieldShape: queueKeys,
    meta: 'queue',
    description: 'Queue of this annotation',
  },
  {
    accessor: 'workspace',
    fieldShape: workspaceKeys,
    meta: 'workspace',
    description: 'Workspace of this annotation',
  },
  {
    accessor: 'annotation',
    fieldShape: annotationKeys,
    meta: 'annotation',
    description: 'The annotation itself (without its content)',
  },
  {
    accessor: 'document',
    fieldShape: documentKeys,
    meta: 'document',
    description: 'Document of this annotation',
  },
  {
    accessor: 'email',
    fieldShape: emailKeys,
    meta: 'email',
    description: 'Email associated with this annotation',
  },
  {
    accessor: 'related_emails',
    isList: true,
    fieldShape: emailKeys,
    meta: 'email',
    description:
      'Emails linked to this annotation (M2M, separate from annotation.email)',
  },
  {
    accessor: 'relations',
    isList: true,
    fieldShape: relationKeys,
    meta: 'relation',
    description: 'Relations the annotation belongs to',
  },
  {
    accessor: 'child_relation',
    fieldShape: relationKeys,
    meta: 'relation',
    description: 'Relation where this annotation is the parent',
  },
  {
    accessor: 'suggested_edit',
    fieldShape: suggestedEditKeys,
    meta: 'suggested_edit',
    description: 'Pending split or page-edit suggestion',
  },
  {
    accessor: 'labels',
    isList: true,
    fieldShape: labelKeys,
    meta: 'label',
    description: 'Labels tagged on this annotation',
  },
  {
    accessor: 'notes',
    isList: true,
    fieldShape: noteKeys,
    meta: 'note',
    description: 'Reviewer notes on this annotation',
  },
  {
    accessor: 'automation_blocker',
    fieldShape: automationBlockerKeys,
    meta: 'automation_blocker',
    description: 'Active automation blocker',
  },
  {
    accessor: 'pages',
    isList: true,
    fieldShape: pageKeys,
    meta: 'page',
    description: "First page of the annotation's document",
  },
  {
    accessor: 'assignees',
    isList: true,
    fieldShape: userKeys,
    meta: 'user',
    description: 'Users assigned to this annotation',
  },
  {
    accessor: 'created_by',
    fieldShape: userKeys,
    meta: 'user',
    description: 'User who created this annotation',
  },
  {
    accessor: 'modified_by',
    fieldShape: userKeys,
    meta: 'user',
    description: 'User who last modified this annotation',
  },
] as const satisfies readonly ContextSideload[];

type Sideload = (typeof CONTEXT_SIDELOADS)[number];

const isList = (sideload: Sideload) => 'isList' in sideload && sideload.isList;

const contextSectionKey = (sideload: Sideload) =>
  `context.${snakeToCamel(sideload.accessor)}` as const;

const accessorPath = (sideload: Sideload) =>
  `context.${sideload.accessor}${isList(sideload) ? '[0]' : ''}`;

const toCompletions = (
  columns: readonly string[],
  meta: string
): VariableCompletion[] =>
  columns.map(column => ({
    caption: column,
    value: column,
    score: 1000,
    meta,
    description: '',
  }));

const prefixed = (
  base: string,
  completions: VariableCompletion[]
): VariableCompletion[] =>
  completions.map(completion => ({
    ...completion,
    value: `${base}.${completion.value}`,
  }));

const sideloadsWithBareCompletions = CONTEXT_SIDELOADS.map(sideload => ({
  sideload,
  bareCompletions: toCompletions(sideload.fieldShape, sideload.meta),
}));

export const contextSections = sideloadsWithBareCompletions.map(
  ({ sideload, bareCompletions }) => ({
    key: contextSectionKey(sideload),
    items: prefixed(accessorPath(sideload), bareCompletions),
  })
);

export const contextTopLevelCompletions: VariableCompletion[] =
  CONTEXT_SIDELOADS.map(sideload => {
    const accessor = `${sideload.accessor}${isList(sideload) ? '[0]' : ''}`;
    return {
      caption: accessor,
      value: accessor,
      score: 1000,
      meta: 'context',
      description: sideload.description,
    };
  });

export const contextCompleterRules = sideloadsWithBareCompletions.map(
  ({ sideload, bareCompletions }) => {
    const pattern = new RegExp(
      `context\\.${sideload.accessor}${isList(sideload) ? '\\[\\d+\\]' : ''}$`
    );
    return {
      test: (token: string) => pattern.test(token),
      completions: bareCompletions,
    };
  }
);
